/**
 * A popup that hangs off a control but lives outside it in the DOM.
 *
 * **A dropdown inside a modal cannot be an ordinary absolutely-positioned child.** The modal body
 * is `overflow-y-auto`, and an absolute element inside it counts toward that box's scrollable
 * area — so opening a picker near the foot of a form grows the dialog's scroll height, the modal
 * gains a scrollbar and jumps, and the list the user is reading moves under them as they reach for
 * it. Worse on a short form, where the dialog was not scrollable at all until the moment somebody
 * opened a dropdown in it: the whole window starts moving in response to a click that was meant to
 * reveal three options. The other half of the same problem is clipping — a list longer than the
 * remaining space is cut off at the dialog's edge, with only a scrollbar to say the rest exists.
 *
 * So the popup is **moved to `document.body` and positioned against the trigger's viewport rect**,
 * which is the only arrangement that answers both: a child of `body` is in nobody's scroll box and
 * inside nobody's `overflow`. Two consequences the callers have to know about, and both are why
 * this is one file rather than a snippet copied into each picker:
 *
 * - **It is no longer inside the control**, so a `focusout` guard asking "did focus stay within my
 *   wrapper" answers no the moment the search box takes it. Every caller passes the popup to that
 *   test as well. Getting this wrong closes the list in the middle of opening it.
 * - **It no longer moves with the page**, because `fixed` is relative to the viewport rather than
 *   to whatever is scrolling. So scrolling is listened for **in the capture phase** — a scroll
 *   inside the modal body does not bubble to `window`, and without capture the list would sit
 *   still while the field it belongs to slid away behind it.
 *
 * `z-index` is stated here rather than left to a utility class: flowbite's dialog is `z-50` and its
 * backdrop `z-40`, so a popup that has left the dialog has to clear both or it opens *underneath*
 * the form it belongs to — which reads as the dropdown not opening at all.
 */

/** Above flowbite's dialog (`z-50`) and its backdrop (`z-40`). */
const POPUP_Z_INDEX = '60';

/** The breathing space between the trigger and its list, matching the old `mt-1`. */
const GAP = 4;

/**
 * How close to the viewport edge the list may sit before it is flipped above the trigger.
 *
 * A margin rather than zero: a list that ends exactly on the fold is one whose last option cannot
 * be clicked on a phone, where the browser's own chrome overlaps the bottom of the viewport.
 */
const EDGE_MARGIN = 8;

export interface AnchoredPopupOptions {
	/** The control the popup belongs to. Its rect is what the popup is placed against. */
	anchor: HTMLElement | undefined;
}

/**
 * Moves `node` to `document.body` and keeps it pinned under (or over) `anchor`.
 *
 * The popup takes the anchor's **width**, so the list lines up with the field it belongs to rather
 * than sizing itself to its longest option — a list wider than its own control reads as a second,
 * unrelated panel.
 */
export function anchoredPopup(node: HTMLElement, options: AnchoredPopupOptions) {
	let anchor = options.anchor;

	function place(): void {
		if (anchor === undefined) return;
		const rect = anchor.getBoundingClientRect();

		node.style.position = 'fixed';
		node.style.zIndex = POPUP_Z_INDEX;
		node.style.left = `${rect.left}px`;
		node.style.width = `${rect.width}px`;

		/*
		 * Below by default and above only when there is genuinely no room, rather than whichever
		 * side has more space: a list that opens upward when it did not need to moves the option
		 * the reader is aiming at to the other side of the control they just clicked.
		 *
		 * `offsetHeight` is read after the popup has been placed, so it is the real rendered
		 * height — the list has its own `max-height`, and guessing from the option count would
		 * flip a list that fits.
		 */
		const below = rect.bottom + GAP;
		const height = node.offsetHeight;
		const overflowsBelow = below + height + EDGE_MARGIN > window.innerHeight;
		const fitsAbove = rect.top - GAP - height > EDGE_MARGIN;

		node.style.top = overflowsBelow && fitsAbove
			? `${rect.top - GAP - height}px`
			: `${below}px`;
	}

	document.body.appendChild(node);
	place();

	/*
	 * Capture, because a scroll inside the modal body — or inside any other scrolling container
	 * between the control and the page — does not bubble. Listening on `window` alone catches only
	 * the page scroll, which is the one case a modal has already prevented.
	 */
	window.addEventListener('scroll', place, true);
	window.addEventListener('resize', place);

	return {
		update(next: AnchoredPopupOptions): void {
			anchor = next.anchor;
			place();
		},
		destroy(): void {
			window.removeEventListener('scroll', place, true);
			window.removeEventListener('resize', place);
			// Svelte removes the node itself when the `{#if}` closes, and it looks the node's
			// current parent up rather than remembering where it put it — so this is the ordinary
			// path only when the action is torn down some other way. `remove()` on a node already
			// detached does nothing.
			node.remove();
		}
	};
}
